<script lang="ts" setup>
import Card from 'primevue/card'
import FormSection from '@/components/forms/FormSection.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import { Icon } from '@iconify/vue'
import { getProductList } from '@/api/products'
import { CurrencyType } from '@/types/currency-types'
import { unitOptions } from '@/constants/dimension'
import { type Customer } from '@/types/customer-types'
import { type Product } from '@/types/product-types'

const props = defineProps<{
  productFields: any
  formPath: string
  type: 'add' | 'edit' | 'view'
  showRemoveButton: boolean
  removeProduct: () => void
  newProduct: () => void
  setErrors: (errors: any) => void
  customerList: Customer[]
  values: any
}>()

const productsList = ref<Product[]>([])
const selectedQualityId = ref<string | null>(null)
const mostRecentCQR = ref<string | null>(null)
const pricePerUnitAmount = ref<number | null>(null)
const pricePerUnitCurrency = ref<number | null>(null)

const isViewPage = computed<boolean>(() => props.type === 'view')
const emit = defineEmits(['updateField'])

const initializeValues = () => {
  const currentProduct = props.values.orderProducts?.[props.formPath]
  if (currentProduct) {
    selectedQualityId.value = currentProduct.qualityId || null
    mostRecentCQR.value = currentProduct.customerQualityReference || null
    pricePerUnitAmount.value = currentProduct.pricePerUnitAmount || currentProduct.costPerUnitAmount || null
    pricePerUnitCurrency.value = currentProduct.pricePerUnitCurrency || currentProduct.costPerUnitCurrency || null
  }
}

onMounted(async () => {
  productsList.value = await getProductList()

  await nextTick()

  initializeValues()

  if (props.type === 'add' && !selectedQualityId.value) {
    updateMostRecentCQR()
  }
})

watch(
    () => props.values.orderProducts?.[props.formPath],
    (newProduct) => {
      if (newProduct && props.type !== 'add') {
        selectedQualityId.value = newProduct.qualityId || null
        mostRecentCQR.value = newProduct.customerQualityReference || null
        pricePerUnitAmount.value = newProduct.pricePerUnitAmount || newProduct.costPerUnitAmount || null
        pricePerUnitCurrency.value = newProduct.pricePerUnitCurrency || newProduct.costPerUnitCurrency || null
      }
    },
    { immediate: true, deep: true }
)

const selectedCustomer = computed(() => {
  return props.customerList.find(
      (customer: { id?: string }) => customer.id === props.values?.customerId
  )
})

const linkedProducts = computed(() => {
  return selectedCustomer.value?.linkedProducts
})

const qualities = computed(() =>
    productsList.value.filter(
        (product) =>
            linkedProducts.value?.some((linkedProduct) => linkedProduct.qualityId === product.id) || false
    )
)

const selectedQuality = computed(() => {
  return productsList.value.find((product) => product.id === selectedQualityId.value) || null
})

const updateFormValues = () => {
  emit('updateField', {
    orderProducts: {
      ...props.values.orderProducts,
      [props.formPath]: {
        ...props.values.orderProducts[props.formPath],
        qualityId: selectedQualityId.value,
        customerQualityReference: mostRecentCQR.value,
        costPerUnitAmount: pricePerUnitAmount.value,
        costPerUnitCurrency: pricePerUnitCurrency.value,
        pricePerUnitAmount: pricePerUnitAmount.value,
        pricePerUnitCurrency: pricePerUnitCurrency.value
      }
    }
  })
}

const updateMostRecentCQR = () => {
  if (!selectedQuality.value) {
    mostRecentCQR.value = null
    pricePerUnitAmount.value = null
    pricePerUnitCurrency.value = null
    return
  }

  const filteredLinkedProducts =
      linkedProducts.value?.filter((product) => product.qualityId === selectedQuality.value?.id) || []

  if (!filteredLinkedProducts.length) {
    mostRecentCQR.value = null
    pricePerUnitAmount.value = null
    pricePerUnitCurrency.value = null
    return
  }

  const sortedProducts = filteredLinkedProducts.sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  )

  let selectedProduct = null

  for (const product of sortedProducts) {
    const startDate = new Date(product.startDate)
    const currentDate = new Date()

    if (startDate > currentDate) {
      continue
    }

    selectedProduct = product
  }

  if (!selectedProduct && sortedProducts.length > 0) {
    selectedProduct = sortedProducts[0]
  }

  mostRecentCQR.value = selectedProduct?.customerQualityReference || null
  pricePerUnitAmount.value = selectedProduct?.costPerUnitAmount || null
  pricePerUnitCurrency.value = selectedProduct?.costPerUnitCurrency ?? null
}

watch(
    selectedQuality,
    () => {
      if (props.type === 'add') {
        updateMostRecentCQR()
      }
    },
    { immediate: false }
)

watch(
    selectedQualityId,
    (newQualityId, oldQualityId) => {
      if (props.type === 'edit' && newQualityId !== oldQualityId && oldQualityId !== null) {
        updateMostRecentCQR()
      }
      updateFormValues()
    }
)

watch([mostRecentCQR, pricePerUnitAmount, pricePerUnitCurrency], () => {
  updateFormValues()
})
</script>

<template>
  <Card>
    <template #content>
      <div class="">
        <FormSection title="" description="">
          <FormInputText
              :viewOnly="isViewPage"
              label="Product ID"
              :inputName="`${formPath}.id`"
              id="id"
              class="hidden"
          />
          <div class="form-group">
            <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="qualityId">Quality</label>
            <div v-if="isViewPage" class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
              <RouterLink
                  :to="`/productSpecification/${selectedQualityId}`"
                  class="font-medium text-neutral-800 hover:underline"
              >
                {{ selectedQuality?.finishedReference || 'No quality selected' }}
              </RouterLink>
            </div>
            <Dropdown
                v-else
                :disabled="isViewPage"
                :optionLabel="'finishedReference'"
                :inputName="`orderProducts.${formPath}.qualityId`"
                :optionValue="'id'"
                :options="qualities"
                id="qualityId"
                v-model="selectedQualityId"
                class="w-full"
            />
          </div>

          <div class="form-group">
            <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="customerQualityReference"
            >Customer Quality Reference</label
            >
            <InputText
                :model-value="mostRecentCQR"
                :inputName="`orderProducts.${formPath}.customerQualityReference`"
                readonly
                disabled
                id="customerQualityReference"
                class="w-full"
            />
          </div>
          <div class="flex w-full items-center gap-2">
            <div class="w-full">
              <FormInputText
                  :model-value="productFields?.totalLengthValue"
                  :viewOnly="isViewPage"
                  label="Total Length"
                  :inputName="`orderProducts.${formPath}.totalLengthValue`"
                  id="totalLengthValue"
                  type="number"
              />
            </div>
            <div class="w-1/4">
              <FormDropdown
                  label="Unit"
                  :viewOnly="isViewPage"
                  :inputName="`orderProducts.${formPath}.totalLengthUnit`"
                  id="totalLengthUnit"
                  :options="unitOptions"
                  optionLabel="label"
                  optionValue="value"
              />
            </div>
          </div>
          <div class="flex w-full items-center gap-2">
            <div class="w-1/4">
              <div v-if="type == 'add'">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="pricePerUnitCurrency">
                  Currency
                </label>
                <Dropdown
                    disabled
                    :inputName="`orderProducts.${formPath}.pricePerUnitCurrency`"
                    optionLabel="symbol"
                    optionValue="id"
                    :options="CurrencyType"
                    id="pricePerUnitCurrency"
                    v-model="pricePerUnitCurrency"
                />
              </div>
              <FormDropdown
                  v-if="type == 'view'"
                  label="Currency"
                  :viewOnly="isViewPage"
                  :inputName="`orderProducts.${formPath}.pricePerUnitCurrency`"
                  optionLabel="symbol"
                  optionValue="id"
                  :options="CurrencyType"
                  id="pricePerUnitCurrency"
                  v-model="pricePerUnitCurrency"
              />
              <div v-if="type == 'edit'" class="form-group">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="pricePerUnitCurrency">
                  Currency
                </label>
                <Dropdown
                    disabled
                    :inputName="`orderProducts.${formPath}.pricePerUnitCurrency`"
                    optionLabel="symbol"
                    optionValue="id"
                    :options="CurrencyType"
                    id="pricePerUnitCurrency"
                    v-model="pricePerUnitCurrency"
                />
              </div>
            </div>
            <div class="form-group w-full">
               <FormInputText
                  :model-value="productFields?.pricePerUnitAmount"
                  readonly
                  disabled
                  label="Price Per Unit"
                  :inputName="`orderProducts.${formPath}.pricePerUnitAmount`"
                  id="pricePerUnitAmount"
                  type="number"
                  class="w-full"
              />
            </div>
          </div>
        </FormSection>
        <Button
            v-show="showRemoveButton"
            label="Delete product"
            iconPos="right"
            @click="removeProduct()"
            class="mb-3"
        >
          <template #icon>
            <Icon icon="ic:baseline-delete" class="text-2xl ml-2 order-1" /> </template
          ></Button>
      </div>
      <Button
          iconPos="right"
          label="Add more product"
          outlined
          v-if="!isViewPage"
          @click="newProduct"
      >
        <template #icon>
          <Icon icon="ic:baseline-plus" class="text-2xl ml-2 order-1" />
        </template>
      </Button>
    </template>
  </Card>
</template>
